import { useEffect, useRef, useState, useCallback } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { deveEnviarTyping } from '@/lib/chat-app';

/** Indicador efémero "a escrever" por conversa, via Supabase Realtime Broadcast.
 *  `who` identifica o emissor; só reage a eventos do outro lado.
 *  Devolve { otherTyping, notifyTyping }. Sem escritas na BD. */
export function useTypingIndicator(clienteId: string | undefined, who: 'consumer' | 'empresa') {
  const [otherTyping, setOtherTyping] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const ultimoEnvioRef = useRef<number | null>(null);
  const expiraRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!clienteId) return;
    const channel = supabase.channel(`typing:${clienteId}`, { config: { broadcast: { self: false } } });
    channel.on('broadcast', { event: 'typing' }, (msg) => {
      if ((msg.payload as { from?: string })?.from === who) return;
      setOtherTyping(true);
      if (expiraRef.current) clearTimeout(expiraRef.current);
      expiraRef.current = setTimeout(() => setOtherTyping(false), 3000);
    });
    channel.subscribe();
    channelRef.current = channel;
    return () => {
      if (expiraRef.current) clearTimeout(expiraRef.current);
      supabase.removeChannel(channel);
      channelRef.current = null;
      ultimoEnvioRef.current = null;
      setOtherTyping(false);
    };
  }, [clienteId, who]);

  const notifyTyping = useCallback(() => {
    const agora = Date.now();
    if (!channelRef.current) return;
    if (!deveEnviarTyping(ultimoEnvioRef.current, agora)) return;
    ultimoEnvioRef.current = agora;
    channelRef.current.send({ type: 'broadcast', event: 'typing', payload: { from: who } });
  }, [who]);

  return { otherTyping, notifyTyping };
}
